// One backlog item: details, screenshots and files (paste anywhere on the page), checklist, linked firm
// feedback, comments (type @ to tag a teammate) and activity; the side panel changes status, where it ships,
// owner and the rest, and who follows it. Opening it marks its comments and your tags on it read (My work).
// An epic lists its items and a story its sub-tasks, each with a quick add; a checklist step can become a sub-task.
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowDown, ArrowLeft, ArrowUp, Bell, BellOff, ChevronRight, CornerDownRight, ImagePlus, Layers, Link2, MoreHorizontal, Paperclip, PenLine, Trash2, X, Zap } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { fmtCalendarDate, fmtDateTime } from '../../lib/format';
import { Avatar, Banner, Button, Card, MenuButton, Pill, ProgressBar, Select, Spinner, TextArea, TextField, cx, initialsOf } from '../../ui';
import { useMe } from '../AdminShell';
import { MessageBody, useFormatting } from '../feedback/Formatting';
import { AREA_LABEL, AREAS, STATUS_LABEL as REQUEST_STATUS, STATUS_TONE as REQUEST_TONE, firmLabel, type QueueRow } from '../feedback/types';
import { useMentionPicker, type Teammate } from '../Mentions';
import { itemEntries, type Handlers, type SetFields } from './actions';
import { ScreenshotViewer, type ViewerFile } from './ScreenshotViewer';
import {
  appendFiles, canSchedule, FILE_ACCEPT, FOUND_IN_LABEL, hotfixName, IMAGE_TYPES, isShipped, itemFile, KIND_LABEL, KIND_TONE, KINDS, levelOf, pastedImages, PRIORITIES, PRIORITY_LABEL,
  ORDER_KEY, PRIORITY_TONE, releaseOption, requestFile, SIZE_LABEL, STATUS_LABEL, STATUS_TONE, STATUSES, useAdminImage, whereLabel,
  type Attachment, type BacklogData, type BacklogMeta, type FirmAttachment, type ItemDetail,
} from './types';

const msg = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);
const EVENT_LABEL: Record<string, string> = {
  created: 'created this', updated: 'updated', scheduled: 'scheduled this', unscheduled: 'took this out of its release', request_linked: 'linked firm feedback',
  request_unlinked: 'unlinked firm feedback', files_added: 'added files', screenshot_marked_up: 'marked up a screenshot', file_updated: 'updated a file',
  file_deleted: 'deleted a file', checklist_added: 'added a checklist step', checklist_updated: 'updated the checklist', checklist_removed: 'removed a checklist step',
  commented: 'commented', created_from_request: 'arrived from firm feedback', followed_request: 'followed its firm request',
  request_reopened_elsewhere: 'saw its firm request reopened on a new item', comment_deleted: 'deleted a comment', archived: 'archived this', restored: 'restored this', merged_into: 'merged this into another item',
  merged_from: 'merged a duplicate into this', hotfix_rolled: 'rolled this in a hotfix', moved_to_next_hotfix: 'carried this to the next hotfix',
  returned_to_backlog: 'returned this to the backlog when the release shipped',
};

export function BacklogItemPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState<ItemDetail | null>(null);
  const [ctx, setCtx] = useState<BacklogMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [viewer, setViewer] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState<'comments' | 'activity'>('comments');
  const filePick = useRef<HTMLInputElement>(null);
  const { reportWorkSeen } = useMe();

  const load = useCallback(async () => {
    try {
      setItem((await api.get<{ item: ItemDetail }>(`/api/admin/backlog/${id}`)).item);
      setError(null);
      api.post(`/api/admin/backlog/${id}/seen`).then(() => reportWorkSeen(), () => {});
    } catch (e) { setError(msg(e, 'Could not load the item.')); }
  }, [id, reportWorkSeen]);
  useEffect(() => { setItem(null); void load(); }, [load]);
  useEffect(() => { api.get<BacklogMeta>('/api/admin/backlog/meta').then(setCtx, () => {}); }, []);

  const save = useCallback(async (body: SetFields | Record<string, unknown>, done?: string) => {
    setError(null);
    try { setItem((await api.patch<{ item: ItemDetail }>(`/api/admin/backlog/${id}`, body)).item); if (done) setNotice(done); return true; }
    catch (e) { setError(msg(e, 'That change could not be saved.')); return false; }
  }, [id]);
  const call = useCallback(async (method: 'post' | 'put' | 'patch' | 'del', path: string, body?: unknown) => {
    setError(null);
    try {
      const res = method === 'del' ? await api.del<{ item: ItemDetail }>(`/api/admin/backlog/${id}${path}`) : await api[method]<{ item: ItemDetail }>(`/api/admin/backlog/${id}${path}`, body);
      setItem(res.item);
      return true;
    } catch (e) { setError(msg(e, 'That change could not be saved.')); return false; }
  }, [id]);
  const upload = useCallback(async (files: File[]) => {
    if (!files.length) return;
    const form = new FormData();
    appendFiles(form, files.map((file) => ({ file })));
    if (await call('post', '/attachments', form)) setNotice(`${files.length === 1 ? (IMAGE_TYPES.includes(files[0]!.type) ? 'Screenshot' : 'File') : `${files.length} files`} added.`);
  }, [call]);

  // Paste a screenshot anywhere on the page (outside a text box) to attach it; J / K step through the list.
  useEffect(() => {
    const paste = (e: ClipboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (viewer !== null || t?.closest('input, textarea, [role="dialog"]')) return;
      const imgs = pastedImages(e);
      if (imgs.length) { e.preventDefault(); void upload(imgs); }
    };
    window.addEventListener('paste', paste);
    return () => window.removeEventListener('paste', paste);
  }, [upload, viewer]);

  if (!item) return error ? <Banner tone="error" role="alert">{error}</Banner> : <Spinner label="Loading the item" />;
  const current = item.attachments.filter((a) => !a.replaced_at);
  const viewerFiles: ViewerFile[] = [...current.map((own) => ({ own })), ...item.firm_attachments.map((firm) => ({ firm }))];
  const shipped = isShipped(item);
  const order: string[] = (() => { try { return JSON.parse(sessionStorage.getItem(ORDER_KEY) ?? '[]') as string[]; } catch { return []; } })();
  const at = order.indexOf(item.id);

  const handlers: Handlers | null = ctx ? {
    update: (_ids, set) => void save(set),
    archive: (_ids, on) => void save({ archived: on }, on ? 'Archived. It no longer shows in the backlog.' : 'Restored to the backlog.'),
    move: (_id, to) => { void api.post(`/api/admin/backlog/${item.id}/move`, { to }).then(() => setNotice(`Moved to the ${to} of the backlog.`), (e) => setError(msg(e, 'Could not move it.'))); },
    merge: () => {},
    open: () => {},
    attach: () => filePick.current?.click(),
    copy: (text, what) => { void navigator.clipboard.writeText(text).then(() => setNotice(`${what} copied.`)); },
  } : null;
  const mergeInto = async () => {
    const ref = window.prompt(`Merge ${item.reference} into which item? Type its BL- number.`)?.trim().toUpperCase();
    if (!ref) return;
    const target = (await api.get<BacklogData>('/api/admin/backlog')).items.find((i) => i.reference === ref);
    if (!target) { setError(`${ref} was not found in the open backlog.`); return; }
    try { await api.post(`/api/admin/backlog/${item.id}/merge`, { into_id: target.id }); navigate(`/admin/backlog/${target.id}`); }
    catch (e) { setError(msg(e, 'Could not merge.')); }
  };

  return (
    <div className="mx-auto flex w-full max-w-shell flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Link to="/admin/backlog" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand no-underline"><ArrowLeft aria-hidden size={16} /> Backlog</Link>
        {at >= 0 && <span className="text-[13px] text-muted">· {at + 1} of {order.length}</span>}
        <span className="ml-auto flex flex-wrap items-center gap-1.5">
          {at >= 0 && <>
            <Button size="sm" disabled={at === 0} onClick={() => navigate(`/admin/backlog/${order[at - 1]}`)}><ArrowUp aria-hidden size={14} /> Previous</Button>
            <Button size="sm" disabled={at === order.length - 1} onClick={() => navigate(`/admin/backlog/${order[at + 1]}`)}><ArrowDown aria-hidden size={14} /> Next</Button>
          </>}
          <Button size="sm" onClick={() => void navigator.clipboard.writeText(window.location.href).then(() => setNotice('Link copied.'))}><Link2 aria-hidden size={14} /> Copy link</Button>
          {ctx && handlers && (
            <MenuButton label="More actions" entries={() => [
              ...itemEntries([item], { ...ctx, items: [] }, handlers).filter((e) => !('label' in e) || (e.label !== 'Open' && e.label !== 'Open in a new tab')),
              ...(!item.archived_at ? [{ kind: 'separator' as const }, { label: 'Merge into another item…', onSelect: () => void mergeInto() }] : []),
            ]} className="inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-line-btn bg-white px-3 text-sm font-semibold text-ink hover:bg-panel">
              <MoreHorizontal aria-hidden size={15} /> More
            </MenuButton>
          )}
        </span>
      </div>
      {error && <Banner tone="error" role="alert">{error}</Banner>}
      {notice && <Banner tone="success" role="status">{notice}</Banner>}
      {item.archived_at && <Banner tone="warn">Archived{item.duplicate_of_reference ? ` as a duplicate of ${item.duplicate_of_reference}` : ''}. <button type="button" onClick={() => void save({ archived: false }, 'Restored to the backlog.')} className="border-0 bg-transparent p-0 font-semibold text-brand underline">Restore</button></Banner>}

      <div className="flex flex-wrap items-start gap-4">
        <div className="flex min-w-0 flex-[999_1_540px] flex-col gap-3">
          {item.parent && (
            <Link to={`/admin/backlog/${item.parent_id}`} className="inline-flex w-fit items-center gap-1.5 text-[13px] font-semibold text-ink-2 no-underline hover:text-brand">
              {item.parent.kind === 'epic' ? <Layers aria-hidden size={14} /> : <CornerDownRight aria-hidden size={14} />}
              {item.parent.kind === 'epic' ? 'Epic' : 'Sub-task of'} <span className="text-brand">{item.parent.reference}</span> {item.parent.title}<ChevronRight aria-hidden size={13} />
            </Link>
          )}
          <Header item={item} editing={editing} setEditing={setEditing} save={save} />
          {levelOf(item) !== 'subtask' && <Children item={item} onError={setError} onChanged={load} />}

          <Card className="p-5">
            <div className="mb-3 flex flex-wrap items-baseline gap-2">
              <h2 className="m-0 text-[15px] font-bold">Screenshots and files · {viewerFiles.length}</h2>
              <span className="text-xs text-muted">Paste a screenshot anywhere on this page to add one. Click to open, mark up or blur.</span>
            </div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
              {current.map((a) => <Tile key={a.id} item={item} own={a} onOpen={() => setViewer(viewerFiles.findIndex((f) => f.own?.id === a.id))} />)}
              {item.firm_attachments.map((a) => <Tile key={a.id} item={item} firm={a} onOpen={() => setViewer(viewerFiles.findIndex((f) => f.firm?.id === a.id))} />)}
              <DropTile onFiles={(f) => void upload(f)} onPick={() => filePick.current?.click()} />
            </div>
            <input ref={filePick} type="file" multiple accept={FILE_ACCEPT} className="hidden" onChange={(e) => { void upload([...(e.target.files ?? [])]); e.target.value = ''; }} />
          </Card>

          <Checklist item={item} team={ctx?.team ?? []} call={call} onSubtask={levelOf(item) === 'story' ? async (step) => {
            try {
              // A step takes 300 characters; a title takes 160.
              await api.post('/api/admin/backlog', { kind: 'task', title: step.text.slice(0, 160).trim(), parent_id: item.id, owner_id: step.owner_id });
              await api.del(`/api/admin/backlog/${item.id}/checklist/${step.id}`);
              await load();
              setNotice(`"${step.text}" is now a sub-task.`);
            } catch (e) { setError(msg(e, 'Could not make it a sub-task.')); }
          } : undefined} />
          <Feedback item={item} call={call} />

          <Card className="px-5 pb-4 pt-2">
            <div role="tablist" className="mb-3 flex gap-4 border-b border-line-soft">
              {(['comments', 'activity'] as const).map((t) => (
                <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
                  className={cx('min-h-9 border-0 border-b-2 bg-transparent px-1 text-[13px] font-semibold', tab === t ? 'border-brand text-ink' : 'border-transparent text-muted')}>
                  {t === 'comments' ? `Comments ${item.comments.length}` : 'Activity'}
                </button>
              ))}
            </div>
            {tab === 'comments' ? <Comments item={item} team={ctx?.team ?? []} onItem={setItem} onError={setError} onOpen={(aid) => setViewer(viewerFiles.findIndex((f) => f.own?.id === aid))} />
              : (
                <ul className="m-0 flex list-none flex-col gap-1.5 p-0 text-[13px]">
                  {item.events.map((e) => <li key={e.id}><span className="font-semibold">{e.actor_name ?? 'System'}</span> {EVENT_LABEL[e.type] ?? e.type.replace(/_/g, ' ')}{eventDetail(e)} <span className="text-xs text-muted">· {fmtDateTime(e.at)}</span></li>)}
                </ul>
              )}
          </Card>
        </div>

        <aside className="flex min-w-0 flex-[1_1_300px] flex-col gap-3">
          <Card className="flex flex-col gap-3 p-4">
            <Select label="Status" small value={item.status} onChange={(e) => void save({ status: e.target.value })}>
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </Select>
            {shipped ? (
              <div className="text-[13px]"><span className="text-xs font-semibold text-ink-2">Ships in</span><div className="mt-1 font-semibold">{item.release_kind === 'hotfix' ? `Live in ${hotfixName(item.release_name)}` : `Shipped in ${item.release_name}`}</div></div>
            ) : !canSchedule(item) ? (
              <div className="text-[13px]">
                <span className="text-xs font-semibold text-ink-2">Ships in</span>
                <div className="mt-1">{item.kind === 'epic'
                  ? <>An epic isn't scheduled. Its items ship in: <b>{[...new Set(item.children.filter((k) => k.release_id).map(whereLabel))].join(' · ') || 'none yet'}</b></>
                  : <>With its parent, <Link to={`/admin/backlog/${item.parent_id}`} className="font-semibold">{item.parent?.reference}</Link></>}</div>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <Select label="Ships in" small value={item.release_id ?? ''} onChange={(e) => void save({ release_id: e.target.value || null })}>
                  <option value="">Not scheduled</option>
                  {(ctx?.releases ?? []).map((r) => <option key={r.id} value={r.id}>{releaseOption(r)}</option>)}
                  {item.release_id && !(ctx?.releases ?? []).some((r) => r.id === item.release_id) && <option value={item.release_id}>{item.release_name}</option>}
                </Select>
                <span className="text-xs">
                  {item.release_id && item.release_kind === 'release' && <Link to={`/admin/releases/${item.release_id}`} className="font-semibold">Open {item.release_name}</Link>}
                  {ctx && item.release_id !== ctx.hotfix_box_id && <> {item.release_id && '· '}<span className="text-muted">Ready sooner?</span> <button type="button" onClick={() => void save({ release_id: ctx.hotfix_box_id }, 'Added to the next hotfix.')} className="border-0 bg-transparent p-0 font-semibold text-warn-fg"><Zap aria-hidden size={11} className="inline" /> Move to next hotfix</button></>}
                </span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2.5">
              <Select label="Type" small value={item.kind} onChange={(e) => void save({ kind: e.target.value })}>{KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}</Select>
              <Select label="Priority" small value={item.priority} onChange={(e) => void save({ priority: e.target.value })}>{PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>)}</Select>
              <Select label="Size" small value={item.size ?? ''} onChange={(e) => void save({ size: e.target.value || null })}><option value="">Not set</option>{(['s', 'm', 'l'] as const).map((s) => <option key={s} value={s}>{SIZE_LABEL[s]}</option>)}</Select>
              <Select label="Found in" small value={item.found_in ?? ''} onChange={(e) => void save({ found_in: e.target.value || null })}><option value="">Not set</option>{(Object.keys(FOUND_IN_LABEL) as Array<keyof typeof FOUND_IN_LABEL>).map((k) => <option key={k} value={k}>{FOUND_IN_LABEL[k]}</option>)}</Select>
            </div>
            <Select label="Area" small value={item.area ?? ''} onChange={(e) => void save({ area: e.target.value || null })}><option value="">Not set</option>{AREAS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select>
            {levelOf(item) === 'story' && (
              <Select label="Epic" small value={item.parent_id ?? ''} onChange={(e) => void save({ parent_id: e.target.value || null })}>
                <option value="">No epic</option>
                {(ctx?.epics ?? []).map((ep) => <option key={ep.id} value={ep.id}>{ep.reference} · {ep.title}</option>)}
              </Select>
            )}
            {levelOf(item) === 'subtask' && (
              <div className="text-[13px]"><span className="text-xs font-semibold text-ink-2">Parent</span>
                <div className="mt-1 flex items-center gap-2"><Link to={`/admin/backlog/${item.parent_id}`} className="min-w-0 truncate font-semibold">{item.parent?.reference} · {item.parent?.title}</Link>
                  <button type="button" onClick={() => void save({ parent_id: null }, 'Moved out. It is its own item now.')} className="ml-auto flex-none border-0 bg-transparent p-0 text-xs font-semibold text-brand">Move out</button></div>
              </div>
            )}
            <Select label="Owner" small value={item.owner_id ?? ''} onChange={(e) => void save({ owner_id: e.target.value || null })}><option value="">Unassigned</option>{(ctx?.team ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</Select>
            <Labels item={item} save={save} />
          </Card>
          <Followers item={item} me={ctx?.me ?? null} call={call} />
          <Card className="p-4 text-[13px]">
            <div className="mb-1 font-bold">{whenTitle(item)}</div>
            <div className="text-muted">{whenBody(item)}</div>
          </Card>
          <div className="px-1 text-xs text-muted">Created {fmtDateTime(item.created_at)}{item.created_by_name ? ` by ${item.created_by_name}` : ''} · updated {fmtDateTime(item.updated_at)}</div>
        </aside>
      </div>
      {viewer !== null && viewer >= 0 && (
        <ScreenshotViewer item={item} files={viewerFiles} index={Math.min(viewer, viewerFiles.length - 1)} onIndex={setViewer} onClose={() => setViewer(null)} onItem={setItem} />
      )}
    </div>
  );
}

function eventDetail(e: ItemDetail['events'][number]) {
  const p = e.payload ?? {};
  if (e.type === 'scheduled' && typeof p.to === 'string') return ` for ${p.to === 'Next hotfix' ? 'the next hotfix' : p.to}`;
  if (e.type === 'hotfix_rolled' && typeof p.name === 'string') return ` (${p.name}${p.toldNow ? ', firms told now' : ''})`;
  if (e.type === 'commented' && typeof p.tagged === 'number') return ` and tagged ${p.tagged === 1 ? 'a teammate' : `${p.tagged} teammates`}`;
  if (e.type === 'updated') return `: ${Object.keys(p).map((k) => k.replace('_id', '').replace('_', ' ')).join(', ')}`;
  return '';
}

function whenTitle(i: ItemDetail) {
  if (i.kind === 'epic') return i.children_total ? `${i.children_done} of ${i.children_total} items done` : 'No items yet';
  if (levelOf(i) === 'subtask') return `Ships with ${i.parent?.reference ?? 'its parent'}`;
  if (i.release_kind === 'hotfix') return i.release_status === 'shipped' ? `Live in ${hotfixName(i.release_name)}` : 'When the hotfix rolls';
  if (i.release_status === 'shipped') return `Shipped in ${i.release_name}`;
  return i.release_id ? `When ${i.release_name} ships` : 'Not scheduled yet';
}
function whenBody(i: ItemDetail) {
  if (i.kind === 'epic') return 'Put its items in releases one by one. Firms hear about each item that carries their request when it ships.';
  if (levelOf(i) === 'subtask') return 'A sub-task is part of its parent. It is not scheduled or announced on its own.';
  const firms = i.requests.length ? `${i.requests.map((r) => r.reference).join(', ')} ${i.requests.length === 1 ? 'is' : 'are'} marked Done and ${i.requests.length === 1 ? 'that firm is' : 'those firms are'} told` : 'No firm requests are linked';
  if (i.release_kind === 'hotfix' && i.release_status !== 'shipped') return `It goes live quietly. ${i.requests.length ? `${i.requests.map((r) => r.reference).join(', ')} stay open until the next release ships, unless you choose to tell the firm when you roll it.` : 'It is announced with the next release.'}`;
  if (i.release_kind === 'hotfix') return i.told_early_at ? 'Live. The linked firms were told when it rolled.' : 'Live. It is announced, and linked firms are told, when the next release ships.';
  if (i.release_status === 'shipped') return 'Shipped and announced.';
  if (i.release_id) return `If it is Done, it ships with the release: ${firms}. If not, it comes back to the backlog, still linked. Screenshots stay internal.`;
  return 'Put it in a release, or in the next hotfix to get it out sooner.';
}

function Header({ item, editing, setEditing, save }: { item: ItemDetail; editing: boolean; setEditing: (v: boolean) => void; save: (b: Record<string, unknown>, done?: string) => Promise<boolean> }) {
  const [f, setF] = useState({ title: item.title, description: item.description ?? '', steps: item.steps ?? '', expected: item.expected ?? '', actual: item.actual ?? '' });
  useEffect(() => { setF({ title: item.title, description: item.description ?? '', steps: item.steps ?? '', expected: item.expected ?? '', actual: item.actual ?? '' }); }, [item, editing]);
  const bug = item.kind === 'bug_fix';
  // Notes and descriptions take the message formatting (bold, lists, links) and show formatted.
  const descBox = useRef<HTMLTextAreaElement>(null);
  const formatting = useFormatting(descBox, (description) => setF((x) => ({ ...x, description })));
  const submit = async () => { if (await save({ title: f.title.trim(), description: f.description.trim() || null, steps: f.steps.trim() || null, expected: f.expected.trim() || null, actual: f.actual.trim() || null })) setEditing(false); };
  const block = (label: string, text: string | null) => text ? <div><p className="m-0 mb-0.5 text-xs font-bold uppercase tracking-[0.04em] text-muted">{label}</p><p className="m-0 whitespace-pre-wrap text-sm">{text}</p></div> : null;
  return (
    <Card className="flex flex-col gap-3 p-5">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-bold text-brand">{item.reference}</span>
        <Pill tone={KIND_TONE[item.kind]}>{levelOf(item) === 'subtask' ? `Sub-task · ${KIND_LABEL[item.kind]}` : KIND_LABEL[item.kind]}</Pill>
        <Pill tone={PRIORITY_TONE[item.priority]}>{PRIORITY_LABEL[item.priority]}</Pill>
        <span className="text-muted">{[item.area && AREA_LABEL[item.area], item.found_in && `found in ${FOUND_IN_LABEL[item.found_in]}`, item.created_by_name && `added by ${item.created_by_name}`].filter(Boolean).join(' · ')}</span>
      </div>
      {editing ? (
        <div className="flex flex-col gap-3" onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') void submit(); if (e.key === 'Escape') setEditing(false); }}>
          <TextField label="Title" value={f.title} maxLength={160} onChange={(e) => setF({ ...f, title: e.target.value })} autoFocus />
          {bug ? (
            <>
              <TextArea label="Steps to reproduce" optional rows={2} value={f.steps} onChange={(e) => setF({ ...f, steps: e.target.value })} />
              <div className="grid gap-3 sm:grid-cols-2">
                <TextArea label="Expected" optional rows={2} value={f.expected} onChange={(e) => setF({ ...f, expected: e.target.value })} />
                <TextArea label="What happened" optional rows={2} value={f.actual} onChange={(e) => setF({ ...f, actual: e.target.value })} />
              </div>
            </>
          ) : null}
          <div className="flex flex-col gap-1.5">
            <TextArea ref={descBox} label={bug ? 'Notes' : 'Description'} optional rows={3} value={f.description}
              onChange={(e) => setF({ ...f, description: e.target.value })} onKeyDown={formatting.onKeyDown} />
            {formatting.bar}
          </div>
          <div className="flex gap-2"><Button size="sm" variant="primary" disabled={!f.title.trim()} onClick={() => void submit()}>Save</Button><Button size="sm" onClick={() => setEditing(false)}>Cancel</Button></div>
        </div>
      ) : (
        <>
          <div className="flex items-start gap-2">
            <h1 className="m-0 flex-1 text-xl font-bold leading-tight">{item.title}</h1>
            <Button variant="link" size="sm" onClick={() => setEditing(true)}><PenLine aria-hidden size={14} /> Edit</Button>
          </div>
          {(item.steps || item.expected || item.actual) && <div className="grid gap-3 sm:grid-cols-3">{block('Steps', item.steps)}{block('Expected', item.expected)}{block('What happened', item.actual)}</div>}
          {item.description && <MessageBody text={item.description} className="text-sm leading-relaxed" />}
          {!item.description && !item.steps && !item.expected && !item.actual && <p className="m-0 text-sm text-muted">No details yet. <button type="button" onClick={() => setEditing(true)} className="border-0 bg-transparent p-0 font-semibold text-brand">Add some</button></p>}
        </>
      )}
    </Card>
  );
}

function Tile({ item, own, firm, onOpen }: { item: ItemDetail; own?: Attachment; firm?: FirmAttachment; onOpen: () => void }) {
  const a = (own ?? firm)!;
  const isImg = a.mime_type.startsWith('image/') && a.scan === 'clean';
  const url = useAdminImage(isImg ? (firm ? requestFile(firm.request_id, a.id) : itemFile(item.id, a.id)) : null);
  return (
    <button type="button" onClick={onOpen} className="flex flex-col gap-1 border-0 bg-transparent p-0 text-left text-ink">
      <span className={cx('relative flex aspect-[16/10] w-full items-center justify-center overflow-hidden rounded-[10px] border bg-white', own?.is_cover ? 'border-brand ring-2 ring-brand' : 'border-line')}>
        {url ? <img src={url} alt={own?.caption || a.filename} className="h-full w-full object-cover" />
          : a.scan === 'pending' ? <span className="text-xs text-muted">Checking…</span>
          : a.scan !== 'clean' ? <span className="text-xs font-semibold text-bad">Failed scan</span>
          : <span className="text-lg font-bold text-bad">{a.filename.split('.').pop()?.toUpperCase()}</span>}
        {own?.is_cover && <span className="absolute bottom-1.5 left-1.5 rounded-pill bg-brand px-2 text-[11px] font-bold text-white">Cover</span>}
        {firm && <span className="absolute bottom-1.5 left-1.5 rounded-pill bg-warn-bg px-2 text-[11px] font-bold text-warn-fg">{firm.request_reference}</span>}
        {own?.annotated && <span className="absolute right-1.5 top-1.5 rounded-pill bg-white/90 px-2 text-[11px] font-semibold text-bad">Marked up</span>}
      </span>
      <span className="truncate text-xs font-semibold">{own?.caption || a.filename}</span>
      <span className="truncate text-[11px] text-muted">{firm ? `${firm.uploaded_by_name}${firm.firm_name ? ` · ${firm.firm_name}` : ''}` : `${a.uploaded_by_name} · ${fmtDateTime(a.created_at)}`}{own?.comment_id ? ' · in a comment' : ''}</span>
    </button>
  );
}

function DropTile({ onFiles, onPick }: { onFiles: (f: File[]) => void; onPick: () => void }) {
  const [over, setOver] = useState(false);
  return (
    <button type="button" onClick={onPick}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onFiles([...e.dataTransfer.files]); }}
      className={cx('flex aspect-[16/10] flex-col items-center justify-center gap-1.5 rounded-[10px] border-[1.5px] border-dashed p-2 text-center text-xs text-ink-2', over ? 'border-ok bg-ok-bg' : 'border-brand-ring bg-brand-tint')}>
      <ImagePlus aria-hidden size={22} className="text-brand" />Drop, paste or <span className="font-semibold text-brand underline">choose files</span>
    </button>
  );
}

const CHILD_KINDS = ['feature', 'bug_fix', 'improvement', 'task'] as const;

/** An epic's items or a story's sub-tasks, with progress and a quick add. */
function Children({ item, onError, onChanged }: { item: ItemDetail; onError: (m: string) => void; onChanged: () => Promise<void> }) {
  const epic = item.kind === 'epic';
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<(typeof CHILD_KINDS)[number]>(epic ? 'feature' : 'task');
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try { await api.post('/api/admin/backlog', { kind: epic ? kind : 'task', title: title.trim(), parent_id: item.id }); setTitle(''); await onChanged(); }
    catch (e) { onError(msg(e, 'Could not add it.')); }
    finally { setBusy(false); }
  };
  if (!epic && !item.children.length && item.release_status === 'shipped') return null;
  return (
    <Card className="p-5">
      <div className="mb-2 flex items-center gap-3">
        <h2 className="m-0 text-[15px] font-bold">{epic ? 'Items in this epic' : 'Sub-tasks'}</h2>
        {item.children_total > 0 && <><span className="text-xs text-muted">{item.children_done} of {item.children_total} done</span>
          <ProgressBar pct={Math.round((item.children_done / item.children_total) * 100)} className="max-w-[160px] flex-1" /></>}
      </div>
      {item.children.length > 0 && (
        <ul className="m-0 list-none p-0">
          {item.children.map((k) => (
            <li key={k.id} className="flex flex-wrap items-center gap-2 border-t border-line-soft py-1.5 text-sm first:border-t-0">
              <Link to={`/admin/backlog/${k.id}`} className="min-w-0 flex-1 truncate text-ink no-underline hover:underline">
                <span className="text-xs font-bold text-brand">{k.reference}</span> {k.title}
                {epic && <span className="ml-1.5 text-xs text-muted">{KIND_LABEL[k.kind]}{k.children_total > 0 ? ` · ${k.children_done}/${k.children_total} sub-tasks` : ''}</span>}
              </Link>
              {epic && <span className="text-xs text-muted">{whereLabel(k)}</span>}
              {k.owner_name && <span className="flex items-center gap-1 text-xs text-ink-2"><Avatar initials={initialsOf(k.owner_name)} size={20} tone="soft" />{k.owner_name.split(' ')[0]}</span>}
              <Pill tone={STATUS_TONE[k.status]} dot className="!py-0 !text-xs">{STATUS_LABEL[k.status]}</Pill>
            </li>
          ))}
        </ul>
      )}
      <form className="mt-2 flex gap-2" onSubmit={(e) => { e.preventDefault(); void add(); }}>
        {epic && (
          <select aria-label="Type of the new item" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)} className="h-9 rounded-[10px] border border-line-strong bg-white px-2 text-sm">
            {CHILD_KINDS.map((k2) => <option key={k2} value={k2}>{KIND_LABEL[k2]}</option>)}
          </select>
        )}
        <input value={title} onChange={(e) => setTitle(e.target.value)} disabled={busy} maxLength={160}
          placeholder={epic ? 'Add an item to this epic, then Enter' : 'Add a sub-task, then Enter'} aria-label={epic ? 'New item in this epic' : 'New sub-task'}
          className="h-9 min-w-0 flex-1 rounded-[10px] border border-line-strong px-3 text-sm" />
      </form>
    </Card>
  );
}

type Step = ItemDetail['checklist'][number];
function Checklist({ item, team, call, onSubtask }: {
  item: ItemDetail; team: Array<{ id: string; name: string }>; call: (m: 'post' | 'patch' | 'del', p: string, b?: unknown) => Promise<boolean>;
  /** Turns a step into a sub-task (only on a story). */
  onSubtask?: (step: Step) => Promise<void>;
}) {
  const [text, setText] = useState('');
  const done = item.checklist.filter((k) => k.done_at).length;
  return (
    <Card className="p-5">
      <div className="mb-2 flex items-center gap-3">
        <h2 className="m-0 text-[15px] font-bold">Checklist</h2>
        {item.checklist.length > 0 && <><span className="text-xs text-muted">{done} of {item.checklist.length}</span><ProgressBar pct={Math.round((done / item.checklist.length) * 100)} className="max-w-[160px] flex-1" /></>}
      </div>
      <ul className="m-0 list-none p-0">
        {item.checklist.map((k) => (
          <li key={k.id} className="group flex items-center gap-2.5 border-t border-line-soft py-1.5 text-sm first:border-t-0">
            <input type="checkbox" checked={!!k.done_at} aria-label={k.text} onChange={(e) => void call('patch', `/checklist/${k.id}`, { done: e.target.checked })} className="h-4 w-4 accent-brand" />
            <span className={cx('min-w-0 flex-1', k.done_at && 'text-muted line-through')}>{k.text}</span>
            <select aria-label={`Owner of ${k.text}`} value={k.owner_id ?? ''} onChange={(e) => void call('patch', `/checklist/${k.id}`, { owner_id: e.target.value || null })}
              className="h-7 max-w-[130px] rounded-md border border-transparent bg-transparent px-1 text-xs text-ink-2 hover:border-line-btn">
              <option value="">Unassigned</option>{team.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {onSubtask && !k.done_at && (
              <button type="button" onClick={() => void onSubtask(k)} title="Make it a sub-task with its own owner, status and comments"
                className="flex-none border-0 bg-transparent p-0 text-xs font-semibold text-brand opacity-0 group-hover:opacity-100 focus:opacity-100">Make sub-task</button>
            )}
            <button type="button" aria-label={`Remove ${k.text}`} onClick={() => void call('del', `/checklist/${k.id}`)} className="flex h-7 w-7 items-center justify-center rounded-md border-0 bg-transparent text-muted opacity-0 hover:text-bad group-hover:opacity-100 focus:opacity-100"><Trash2 aria-hidden size={14} /></button>
          </li>
        ))}
      </ul>
      <form className="mt-2 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (text.trim()) void call('post', '/checklist', { text: text.trim() }).then((ok) => ok && setText('')); }}>
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a step, then Enter" aria-label="New checklist step" maxLength={300}
          className="h-9 min-w-0 flex-1 rounded-[10px] border border-line-strong px-3 text-sm" />
      </form>
    </Card>
  );
}

function Feedback({ item, call }: { item: ItemDetail; call: (m: 'post' | 'patch' | 'del', p: string, b?: unknown) => Promise<boolean> }) {
  const [q, setQ] = useState('');
  const [matches, setMatches] = useState<QueueRow[]>([]);
  useEffect(() => {
    if (!q.trim()) { setMatches([]); return; }
    const t = window.setTimeout(() => {
      api.get<{ requests: QueueRow[] }>(`/api/admin/feedback?q=${encodeURIComponent(q.trim())}`).then((r) => setMatches(r.requests.slice(0, 6)), () => setMatches([]));
    }, 250);
    return () => window.clearTimeout(t);
  }, [q]);
  return (
    <Card className="p-5">
      <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="m-0 text-[15px] font-bold">Firm feedback this covers</h2>
        <span className="text-xs text-muted">{item.release_kind === 'hotfix' ? 'Firms hear when the next release ships, unless told when the hotfix rolls.' : 'Firms see the release date as their estimate and hear when it ships.'}</span>
      </div>
      {item.requests.length === 0 && <p className="m-0 py-1 text-[13px] text-muted">None linked yet.</p>}
      <ul className="m-0 list-none p-0">
        {item.requests.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-t border-line-soft py-2 text-[13px] first:border-t-0">
            <Link to={`/admin/feedback/${r.id}`} className="font-bold text-brand">{r.reference}</Link>
            <span className="min-w-[180px] flex-1">{r.title}</span>
            <span className="text-muted">{r.firm_name ?? r.firm_key} · {r.submitted_by_name}</span>
            <Pill tone={REQUEST_TONE[r.status]} dot>{REQUEST_STATUS[r.status]}{r.estimated_completion ? ` · est. ${fmtCalendarDate(r.estimated_completion)}` : ''}</Pill>
            <button type="button" onClick={() => void call('del', `/requests/${r.id}`)} className="border-0 bg-transparent p-0 text-[13px] font-semibold text-brand">Unlink</button>
          </li>
        ))}
      </ul>
      <div className="mt-2 flex max-w-[420px] flex-col gap-1.5">
        <TextField label="Link another request" small placeholder="FB- number or words" value={q} onChange={(e) => setQ(e.target.value)} />
        {matches.length > 0 && (
          <ul className="m-0 list-none divide-y divide-line-soft rounded-[10px] border border-line p-0">
            {matches.filter((m) => !item.requests.some((r) => r.id === m.id)).map((m) => (
              <li key={m.id}><button type="button" onClick={() => { void call('post', '/requests', { request_id: m.id }); setQ(''); }}
                className="w-full border-0 bg-transparent px-3 py-2 text-left text-[13px] hover:bg-row-hi"><span className="font-bold text-brand">{m.reference}</span> {m.title} · {firmLabel(m)}</button></li>
            ))}
          </ul>
        )}
      </div>
      {(item.duplicates.length > 0 || item.duplicate_of_reference) && (
        <p className="m-0 mt-3 text-xs text-muted">
          {item.duplicates.length > 0 && <>Merged into this: {item.duplicates.map((d, n) => <span key={d.id}>{n > 0 && ', '}<Link to={`/admin/backlog/${d.id}`}>{d.reference}</Link></span>)}</>}
          {item.duplicate_of_id && <> Duplicate of <Link to={`/admin/backlog/${item.duplicate_of_id}`}>{item.duplicate_of_reference}</Link></>}
        </p>
      )}
    </Card>
  );
}

function Labels({ item, save }: { item: ItemDetail; save: (b: Record<string, unknown>) => Promise<boolean> }) {
  const [adding, setAdding] = useState(false);
  const [v, setV] = useState('');
  const add = () => { const t = v.trim().toLowerCase(); if (t && !item.labels.includes(t)) void save({ labels: [...item.labels, t] }); setV(''); setAdding(false); };
  return (
    <div className="flex flex-col gap-1 text-xs font-semibold text-ink-2">Labels
      <div className="flex flex-wrap items-center gap-1.5">
        {item.labels.map((l) => (
          <span key={l} className="inline-flex items-center gap-1 rounded-pill bg-subtle py-0.5 pl-2 pr-1 text-xs font-semibold text-ink-2">{l}
            <button type="button" aria-label={`Remove label ${l}`} onClick={() => void save({ labels: item.labels.filter((x) => x !== l) })} className="flex h-4 w-4 items-center justify-center rounded-full border-0 bg-transparent p-0 text-muted"><X aria-hidden size={10} /></button>
          </span>
        ))}
        {adding ? <input autoFocus value={v} maxLength={40} onChange={(e) => setV(e.target.value)} onBlur={add} onKeyDown={(e) => { if (e.key === 'Enter') add(); if (e.key === 'Escape') setAdding(false); }} aria-label="New label" className="h-6 w-28 rounded-md border border-brand px-1.5 text-xs font-normal" />
          : <button type="button" onClick={() => setAdding(true)} className="border-0 bg-transparent p-0 text-xs font-semibold text-brand">+ Add</button>}
      </div>
    </div>
  );
}

/** Who follows the item, and a Follow / Unfollow button for you. */
function Followers({ item, me, call }: { item: ItemDetail; me: string | null; call: (m: 'post' | 'put', p: string, b?: unknown) => Promise<boolean> }) {
  const following = item.watchers.some((w) => w.id === me);
  return (
    <Card className="flex flex-col gap-2 p-4 text-[13px]">
      <div className="flex items-center gap-2">
        <span className="font-bold">Followers · {item.watchers.length}</span>
        {me && (
          <Button size="sm" className="ml-auto" aria-pressed={following} onClick={() => void call('put', '/follow', { watching: !following })}>
            {following ? <><BellOff aria-hidden size={14} /> Unfollow</> : <><Bell aria-hidden size={14} /> Follow</>}
          </Button>
        )}
      </div>
      {item.watchers.length > 0 && (
        <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
          {item.watchers.map((w) => (
            <li key={w.id} className="inline-flex items-center gap-1.5 rounded-pill bg-subtle py-0.5 pl-0.5 pr-2 text-xs">
              <Avatar initials={initialsOf(w.name)} size={20} tone="soft" />{w.id === me ? 'You' : w.name}
            </li>
          ))}
        </ul>
      )}
      <span className="text-xs text-muted">Followers see new comments in My work. Tagging someone, or making them the owner, adds them.</span>
    </Card>
  );
}

function Comments({ item, team, onItem, onError, onOpen }: { item: ItemDetail; team: Teammate[]; onItem: (i: ItemDetail) => void; onError: (m: string) => void; onOpen: (attachmentId: string) => void }) {
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const pick = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const formatting = useFormatting(box, setBody);
  const mentions = useMentionPicker(box, setBody, team);
  const send = async () => {
    if (!body.trim() && !files.length) return;
    setBusy(true);
    const form = new FormData();
    form.append('body', mentions.tagged(body.trim()));
    if (files.length) appendFiles(form, files.map((file) => ({ file })));
    try { onItem((await api.post<{ item: ItemDetail }>(`/api/admin/backlog/${item.id}/comments`, form)).item); setBody(''); setFiles([]); }
    catch (e) { onError(msg(e, 'The comment could not be posted.')); }
    finally { setBusy(false); }
  };
  const remove = async (cid: string) => {
    if (!window.confirm('Delete this comment and its screenshots?')) return;
    try { onItem((await api.del<{ item: ItemDetail }>(`/api/admin/backlog/${item.id}/comments/${cid}`)).item); }
    catch (e) { onError(msg(e, 'Could not delete the comment.')); }
  };
  return (
    <div className="flex flex-col gap-3">
      {item.comments.map((c) => (
        <div key={c.id} className="group flex gap-2.5">
          <Avatar initials={initialsOf(c.author_name)} size={28} tone="soft" />
          <div className="min-w-0 flex-1">
            <div className="text-[13px]"><b>{c.author_name}</b> <span className="text-muted">· {fmtDateTime(c.created_at)}</span>
              <button type="button" onClick={() => void remove(c.id)} className="ml-2 border-0 bg-transparent p-0 text-xs text-muted opacity-0 hover:text-bad group-hover:opacity-100 focus:opacity-100">Delete</button></div>
            {c.body && <MessageBody text={c.body} className="text-sm" />}
            <div className="mt-1.5 flex flex-wrap gap-2">
              {item.attachments.filter((a) => a.comment_id === c.id && !a.replaced_at).map((a) => <CommentShot key={a.id} item={item} a={a} onOpen={() => onOpen(a.id)} />)}
            </div>
          </div>
        </div>
      ))}
      <div className="flex gap-2.5">
        <span className="h-7 w-7 flex-none" />
        <div className="min-w-0 flex-1 rounded-[10px] border border-line-strong px-2.5 py-2"
          onPaste={(e) => { const imgs = pastedImages(e); if (imgs.length) { e.preventDefault(); e.stopPropagation(); setFiles((f) => [...f, ...imgs].slice(0, 10)); } }}>
          <div className="relative">
            <textarea ref={box} value={body} onChange={(e) => { setBody(e.target.value); mentions.sync(); }} rows={2} maxLength={5000} aria-label="Write a comment"
              {...mentions.inputProps}
              onKeyDown={(e) => {
                if (mentions.onKeyDown(e)) return;
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); void send(); return; }
                formatting.onKeyDown(e);
              }}
              placeholder="Write a comment. Type @ to tag a teammate, or paste a screenshot right here." className="w-full resize-y border-0 p-0 text-sm outline-none" />
            {mentions.menu}
          </div>
          <div className="mt-1.5">{formatting.bar}</div>
          {files.length > 0 && <div className="mt-1 flex flex-wrap gap-1.5">{files.map((f, n) => <span key={n} className="inline-flex items-center gap-1 rounded-pill bg-subtle py-0.5 pl-2 pr-1 text-xs">{f.name}<button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles((x) => x.filter((_, i) => i !== n))} className="flex h-4 w-4 items-center justify-center border-0 bg-transparent p-0"><X aria-hidden size={10} /></button></span>)}</div>}
          <div className="mt-2 flex items-center gap-2">
            <Button variant="link" size="sm" onClick={() => pick.current?.click()}><Paperclip aria-hidden size={14} /> Attach</Button>
            <input ref={pick} type="file" multiple accept={FILE_ACCEPT} className="hidden" onChange={(e) => { setFiles((f) => [...f, ...(e.target.files ?? [])].slice(0, 10)); e.target.value = ''; }} />
            <Button size="sm" variant="primary" className="ml-auto" disabled={busy || (!body.trim() && !files.length)} onClick={() => void send()}>{busy ? 'Posting…' : 'Comment'}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CommentShot({ item, a, onOpen }: { item: ItemDetail; a: Attachment; onOpen: () => void }): ReactNode {
  const url = useAdminImage(a.mime_type.startsWith('image/') && a.scan === 'clean' ? itemFile(item.id, a.id) : null);
  return (
    <button type="button" onClick={onOpen} className="h-[90px] w-[150px] overflow-hidden rounded-lg border border-line bg-white p-0">
      {url ? <img src={url} alt={a.filename} className="h-full w-full object-cover" /> : <span className="text-xs text-muted">{a.scan === 'pending' ? 'Checking…' : a.filename}</span>}
    </button>
  );
}
